import { useEffect, useRef } from 'react';
import { StrategyTradeNotificationsChannel } from '@/services/websocket/channels/strategyTradeNotifications';
import { toastManager } from '@/components/common/Toast';
import { chainTokenAPI } from '@/services/api/endpoints/token';
import { CHAINS } from '@/config/chains';
import { STATUS_LABELS } from '@/types/api/quickTrade';
import type { StrategyTradeNotification } from '@/types/api/quickTrade';
import { publishSuccessfulStrategyTradeNotification } from '@/services/websocket/tradeNotificationEvents';

/**
 * Request browser notification permission (once).
 */
function ensureNotificationPermission(): void {
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

/**
 * Fire a browser notification when the tab is not visible.
 */
function sendBrowserNotification(
  notification: StrategyTradeNotification,
  tokenSymbol?: string,
  tokenLogo?: string | null,
): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (!document.hidden) return;

  const chain = CHAINS.find((c) => c.numericId === notification.chain_id);
  const label = tokenSymbol || notification.token_address?.slice(0, 6) || 'Token';
  const side = notification.trade_type === 'sell' ? 'Sell' : 'Buy';
  const statusLabel = STATUS_LABELS[notification.status] || notification.status;

  const n = new Notification(`${side} ${statusLabel} - ${label}`, {
    body: notification.message || notification.strategy_name || 'Trade update',
    icon: tokenLogo || chain?.icon || undefined,
    tag: `trade-${notification.trade_id}`,
  });

  if (chain && notification.token_address) {
    n.onclick = () => {
      window.focus();
      window.location.href = `/data-explorer/${chain.id}/token/${notification.token_address}`;
    };
  }
}

/**
 * Subscribes to strategy trade notifications when authenticated.
 * On each notification: creates or updates a trade toast, fetches token
 * metadata to enrich it, and fires a browser notification when the tab
 * is in the background.
 */
export function useStrategyTradeNotifications(isAuthenticated: boolean) {
  const channelRef = useRef<StrategyTradeNotificationsChannel | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    ensureNotificationPermission();

    const channel = new StrategyTradeNotificationsChannel();
    channelRef.current = channel;

    const unsubscribeChannel = channel.subscribe((notification: StrategyTradeNotification) => {
      publishSuccessfulStrategyTradeNotification(notification);

      const existingToast = toastManager.findToastByTradeId(notification.trade_id);
      const toastId = existingToast?.id || toastManager.createToast({
        kind: 'trade',
        tradeId: notification.trade_id,
        tokenAddress: notification.token_address,
        tradeType: notification.trade_type,
        status: notification.status,
        chainId: notification.chain_id,
        strategyName: notification.strategy_name,
        message: notification.message,
        txHash: notification.tx_hash,
      });

      if (existingToast) {
        toastManager.updateToast(toastId, {
          status: notification.status,
          message: notification.message,
          tx_hash: notification.tx_hash,
        });
      }

      // Fetch token metadata to enrich the toast and fire browser notification
      const chain = CHAINS.find((c) => c.numericId === notification.chain_id);
      if (chain && notification.token_address) {
        chainTokenAPI.getToken(chain.id, notification.token_address)
          .then((res) => {
            const token = res.token;
            if (token) {
              const displaySymbol = token.symbol?.trim() || undefined;
              toastManager.enrichToast(toastId, {
                tokenSymbol: displaySymbol,
                tokenLogo: token.logo,
              });
              sendBrowserNotification(notification, displaySymbol, token.logo || undefined);
            }
          })
          .catch(() => {
            // Still send browser notification with address fallback
            sendBrowserNotification(notification);
          });
      } else {
        sendBrowserNotification(notification);
      }
    });

    return () => {
      unsubscribeChannel();
      channelRef.current = null;
    };
  }, [isAuthenticated]);
}
